// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { FC, useCallback, useEffect, useState } from 'react';

import { HomePage } from './subpages/home';
import { UsersAddPage } from './subpages/users/add';
import { UsersAuthorizePage } from './subpages/users/authorize';
import { UsersListPage } from './subpages/users/list';
import { UsersEditPage } from './subpages/users/edit';
import { UsersPage } from './subpages/users/default';
import { SecurityPage } from './subpages/security/default';
import { SecurityPoliciesPage } from './subpages/security/policies';
import { SecurityPoliciyEditPage } from './subpages/security/policies.edit';
import { SecurityPoliciyAddPage } from './subpages/security/policies.add';
import { SecurityExclusionsPage } from './subpages/security/exclusions';
import { SecurityAssignmentPage } from './subpages/security/assignment';
import { SecurityGeolocalizationPage } from './subpages/security/geolocalization';
import { SecurityBlocksPage } from './subpages/security/blocks';

import { NotificationsListPage } from './subpages/notifications/list';
import { NotificationsPage } from './subpages/notifications/default';
import { NotificationsAddPage } from './subpages/notifications/add';
import { NotificationsManualPage } from './subpages/notifications/manual';

import { InvitationsAddPage } from './subpages/invitations/add';
import { InvitationsListPage } from './subpages/invitations/list';
import { InvitationsPage } from './subpages/invitations/default';
import { InvitationsAuthorizePage } from './subpages/invitations/authorize';

import { LogsAllPage } from './subpages/logs/all';
import { LogsPage } from './subpages/logs/default';

import { InvoicesPage } from './subpages/invoices/default';
import { InvoiceIssuedPage } from './subpages/invoices/issued';
import { InvoicePendingPage } from './subpages/invoices/pendings';
import { InvoicesViewPage } from './subpages/invoices/view';

import { InvoiceNotifyPage } from './subpages/invoices/notify';
import { ConfigPage } from './subpages/config/default';
import { ConfigPropertyPage } from './subpages/config/property';
import { ConfigAccessesPage } from './subpages/config/accesses';
import { ConfigAccessEditPage } from './subpages/config/access.edit';
import { ConfigUnitsPage } from './subpages/config/units';
import { ConfigUnitEditPage } from './subpages/config/unit.edit';
import { ConfigUnitsOrderPage } from './subpages/config/units.order';
import { ConfigSectorsPage } from './subpages/config/sectors';
import { ConfigDevicesPage } from './subpages/config/devices';

import { UserLoginPage } from './subpages/user/login';
import { UserLogoffPage } from './subpages/user/logoff';
import { AdminPage } from './subpages/admin/default';
import { AdminPropertiesPage } from './subpages/admin/properties';
import { AdminUsersPage } from './subpages/admin/users';
import { UsersAddAdminPage } from './subpages/admin/user.add';
import { AdminPasswordPage } from './subpages/admin/password';
import { SamplesTable1Page } from './subpages/samples/table1';
import { useGlobalState } from '../context/GlobalStateContext';
import { CustomAppLayout } from '../../pages/index/layout';

const pages: Record<string, FC> = {
  'users/add': UsersAddPage,
  'users/list': UsersListPage,
  'users/authorize': UsersAuthorizePage,
  'users/edit': UsersEditPage,
  'users': UsersPage,
  'security': SecurityPage,
  'security/policies': SecurityPoliciesPage,
  'security/policies.edit': SecurityPoliciyEditPage,
  'security/policies.add': SecurityPoliciyAddPage,
  'security/exclusions': SecurityExclusionsPage,
  'security/geolocalization': SecurityGeolocalizationPage,
  'security/assignment': SecurityAssignmentPage,
  'security/blocks': SecurityBlocksPage,
  'notifications/list': NotificationsListPage,
  'notifications': NotificationsPage,
  'notifications/add': NotificationsAddPage,
  'notifications/manual': NotificationsManualPage,
  'invitations/add': InvitationsAddPage,
  'invitations/list': InvitationsListPage,
  'invitations/default': InvitationsPage,
  'invitations/authorize': InvitationsAuthorizePage,
  'logs/all': LogsAllPage,
  'logs': LogsPage,
  'invoices': InvoicesPage,
  'invoices/issued': InvoiceIssuedPage,
  'invoices/pendings': InvoicePendingPage,
  'invoices/notify': InvoiceNotifyPage,
  'invoices/view': InvoicesViewPage,
  'config/default': ConfigPage,
  'config/property': ConfigPropertyPage,
  'config/accesses': ConfigAccessesPage,
  'config/access.edit': ConfigAccessEditPage,
  'config/units': ConfigUnitsPage,
  'config/unit.edit': ConfigUnitEditPage,
  'config/units.order': ConfigUnitsOrderPage,
  'config/sectors': ConfigSectorsPage,
  'config/devices': ConfigDevicesPage,
  'user/login': UserLoginPage,
  'user/logoff': UserLogoffPage,
  'admin/properties': AdminPropertiesPage,
  'admin/users': AdminUsersPage,
  'admin/user.add': UsersAddAdminPage,
  'admin/default': AdminPage,
  'admin/password': AdminPasswordPage,
  'home': HomePage,
  'samples/table1': SamplesTable1Page,
};

function NotFound() {
  return <div>Not found</div>;
}

interface RouterProps {
  initialPage: string;
}

// Helper functions (Improved)
function getHashPath(hash: string): string {
  const questionMarkIndex = hash.indexOf('?');
  if (questionMarkIndex !== -1) {
    return hash.substring(1, questionMarkIndex);
  } 
  return hash.substring(1);
}
 


export function Router({ initialPage }: RouterProps) {
  const { returnUrl, setReturnUrl } = useGlobalState();
  const [currentPagePath, setCurrentPage] = useState(getHashPath(window.location.hash) || initialPage);
  const { JWT, setJWT } = useGlobalState(); const { serverVersion, setServerVersion } = useGlobalState();
  const { idPropiedad, setIdPropiedad } = useGlobalState();
  const { pendingAuth, setPendingAuth } = useGlobalState();
  const { invoices, setInvoices } = useGlobalState();
  const { showPropertiesSelector, setShowPropertiesSelector } = useGlobalState();
  const { topNotificationsBadge, setTopNotificationsBadge } = useGlobalState();
  const { reloader, setReloader } = useGlobalState();
  const { invoicesExpired, setInvoicesExpired } = useGlobalState();
  const [initRouter, setInitRouter] = useState(true)
  const { gData, setGData } = useGlobalState();
  const handleValidateStatus = useCallback(async () => {
    if (!JWT || !idPropiedad) return;
    try {
      const response = await fetch('https://api2.llavemovil.com/routing/v1/default.aspx?session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-routing': '/admin/admins/session',
          'JWT': JWT,
        },
        body: JSON.stringify({}),
      });
 
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      if (data.serverVersion)
        setServerVersion(data.serverVersion)
      else
        setServerVersion(0)
      if (data.code == 0) {
        if (data.JWT && data.JWTRenew && data.JWTRenew !== "") {
          if (JWT != data.JWTRenew) {
            setJWT(data.JWTRenew);
          }
        }
        if (data.counters) {
          setPendingAuth(data.counters.pendingAuth);
          setInvoices(data.counters.invoices);
          const shouldShowBadge = data.counters.pendingAuth !== 0 || data.counters.invoices !== 0;
          setTopNotificationsBadge(shouldShowBadge ? 1 : 0);
          if(data && data.admin && data.admin.properties)
            setShowPropertiesSelector(data.admin.properties.length);
        }
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setInitRouter(false)
    }
  }, [initRouter, pendingAuth, invoicesExpired]);

  useEffect(() => {
    const handler = () => {
      const newHash = getHashPath(window.location.hash);
      //const newReturnUrl = getHashParameter(window.location.hash, 'returnUrl');
      //setReturnUrl(newReturnUrl);
      setCurrentPage(newHash || initialPage);
    };

    window.addEventListener('hashchange', handler);
    handler(); //call handler to process initial hash

    return () => window.removeEventListener('hashchange', handler);
  }, [initialPage, setReturnUrl]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPagePath]);

  useEffect(() => {
    const currentHash = getHashPath(window.location.hash) || '';
    if (currentHash.indexOf('user/') === -1) {
      if (JWT === '') {
        window.location.hash = `#user/login?returnUrl=${encodeURIComponent(currentHash)}`;
      } else if (isJWTExpired(JWT)) {
        window.location.hash = `#user/login?returnUrl=${encodeURIComponent(currentHash)}`;
      } else {
        //handleValidateStatus();
      }
    }
  }, [currentPagePath, JWT, isJWTExpired, handleValidateStatus]);

  const CurrentPage = currentPagePath in pages ? pages[currentPagePath] : NotFound;
  return <CurrentPage />;
}

// ... (rest of your isJWTExpired and useRouter functions)
export const isJWTExpired = (token: string) => {
  if (!token) return true;
  try {
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) return true;
    const payloadJson = atob(payloadBase64);
    const payload = JSON.parse(payloadJson);
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch (error) {
    console.error("Error decoding or parsing JWT:", error);
    return true;
  }
};

export function useRouter() {
  return {
    navigate: (newPage: string) => (location.hash = `#${newPage}`),
  };
}
